import React, { useMemo, useState } from 'react';
import { useCouplingStore } from '../../../../store/couplingStore';
import { useZoneStore } from '../../../../store/zoneStore';
import { useReadOnlyUi } from '../../../../store/featureStore';
import { useCouplingSwitch } from '../../../shared/CouplingSwitchDialog';
import { ChevronIcon } from '../../../icons/ButtonIcons';
import { CouplingRow } from './CouplingRow';

interface CouplingsSectionProps {
  /** The switch element (ONS or KVS) whose Trennstellen are listed. */
  elementId: string;
}

/**
 * Trennstellen whose switch/fuse sits in this station (doc/Architektur/NETZVERBUND.md 6).
 * Used by the ONS details; the KVS busbar view lists the same rows per busbar instead.
 * Renders nothing when the element has no Trennstelle.
 */
export const CouplingsSection: React.FC<CouplingsSectionProps> = ({ elementId }) => {
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const couplings = useCouplingStore(s => s.couplings);
  const readOnly = useReadOnlyUi();
  const [isExpanded, setIsExpanded] = useState(true);
  const { requestSwitch, dialog } = useCouplingSwitch();

  const atElement = useMemo(() => couplings.filter(c => c.switchElementId === elementId), [couplings, elementId]);
  if (!activeZoneId || atElement.length === 0) return null;

  const closed = atElement.filter(c => c.isClosed).length;

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 dark:bg-emerald-600" />
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Trennstellen an dieser Station</span>
          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
            {closed} zu · {atElement.length - closed} offen
          </span>
        </div>
        <ChevronIcon direction={isExpanded ? 'up' : 'down'} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
      </button>
      {isExpanded && (
        <div className="pl-4 pr-3 pb-3 space-y-1">
          {atElement.map(c => (
            <CouplingRow key={c.id} coupling={c} activeZoneId={activeZoneId} readOnly={readOnly} onSwitch={target => requestSwitch(c, target)} />
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
};
